import React, { useEffect, useRef, useState } from "react";
import { errorText, PHOTO_TYPES, saveProfile, savePhoto, withChosenName } from "./adjudicatorApi";
import PhotoEditor from "./PhotoEditor";

// The adjudicator's public profile (card 930d3ac0): the photo shown with their
// name and country on event pages and results. Judges go by the name and
// country in the CLRG directory (Devin 2026-10-26), so those are shown, not
// edited: Feis.link corrects them. Used on the invitation page and on
// /adjudicator/profile.

const initialsOf = (profile) =>
  `${(profile?.fname || "").trim().charAt(0)}${(profile?.lname || "").trim().charAt(0)}`.toUpperCase();

// How the judge appears: their photo (tap it, or the button, to choose one
// from the phone's photos or camera; then frame it in PhotoEditor) over the
// name and country as listed (the name changeable with the pencil when the
// page allows it). Nothing is uploaded until the page
// saves it. onPick({ blob, preview }).
// The name shows as listed in the CLRG directory; with onChooseName, a pencil
// beside it lets the judge change how it's shown (Devin 2026-10-27).
const PencilIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm17.71-10.21a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83Z" /></svg>
);
const tidyName = (s) => String(s || "").replace(/\s+/g, " ").trim();
export const FINISH_NAME = "Finish your name first: tap Done or Cancel.";

export const PhotoPicker = ({ preview, profile, onPick, onError, disabled, prompt, chosenName, onChooseName, onNameEditing }) => {
  const [nameDraft, setNameDraft] = useState(null); // { fname, lname } while the pencil is open
  const [nameError, setNameError] = useState("");
  // the boxes close when the page stops offering the pencil (the photo step),
  // and the page knows while they're open (it won't accept a half-edited name)
  const editingName = !!nameDraft && !!onChooseName;
  useEffect(() => { if (!onChooseName) setNameDraft(null); }, [onChooseName]);
  useEffect(() => { if (onNameEditing) onNameEditing(editingName); }, [editingName, onNameEditing]);
  const startName = () => {
    setNameDraft(chosenName || { fname: profile?.fname || "", lname: profile?.lname || "" });
    setNameError("");
  };
  const enterIsDone = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    doneName();
  };
  const doneName = () => {
    const fname = tidyName(nameDraft.fname);
    const lname = tidyName(nameDraft.lname);
    if (!fname || !lname) return setNameError("Enter your first and last name.");
    // back to the name on record: nothing to change
    const same = fname === tidyName(profile?.fname) && lname === tidyName(profile?.lname);
    onChooseName(same ? null : { fname, lname });
    setNameDraft(null);
  };
  const fileRef = useRef(null);
  // Object URLs this picker made: the framed original (for Adjust), a newly
  // chosen one still in the editor, and the framed preview it handed up.
  // Each is revoked when replaced, cancelled or the picker goes away.
  const [source, setSource] = useState(null);
  const [pending, setPending] = useState(null);
  const urls = useRef({ source: null, pending: null, preview: null });
  const swap = (key, url) => {
    if (urls.current[key] && urls.current[key] !== url) URL.revokeObjectURL(urls.current[key]);
    urls.current[key] = url;
  };
  useEffect(() => () => Object.values(urls.current).forEach((u) => u && URL.revokeObjectURL(u)), []);
  const [editing, setEditing] = useState(false);
  const [framing, setFraming] = useState(null); // source's crop and zoom, for Adjust

  const choose = () => fileRef.current?.click();
  const pick = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    onError("");
    // some Android pickers give no type; the editor rejects what won't load
    if (file.type && !PHOTO_TYPES.test(file.type)) {
      onError("Choose a JPEG or PNG photo.");
      return;
    }
    // the current photo and its Adjust stay until this one is framed
    const url = URL.createObjectURL(file);
    swap("pending", url);
    setPending(url);
    setEditing(true);
  };
  const framed = (blob, frame) => {
    setFraming(frame);
    if (pending) {
      swap("source", pending);
      setSource(pending);
      urls.current.pending = null;
      setPending(null);
    }
    const preview = URL.createObjectURL(blob);
    swap("preview", preview);
    setEditing(false);
    onPick({ blob, preview });
  };
  const cancel = () => {
    if (pending) {
      swap("pending", null);
      setPending(null);
    }
    setEditing(false);
  };
  const listedName = profile?.name || `${profile?.fname || ""} ${profile?.lname || ""}`.trim();
  const name = chosenName ? `${chosenName.fname} ${chosenName.lname}` : listedName;

  return (
    <div className="adj-who">
      <button type="button" className="adj-photo" onClick={choose} disabled={disabled}
        aria-label={preview ? "Change your photo" : "Add your photo"}>
        {preview ? <img src={preview} alt="" /> : <span>{initialsOf(chosenName || profile) || "?"}</span>}
        <span className="adj-photo-badge" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M9 3 7.2 5H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3.2L15 3H9Zm3 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" /></svg>
        </span>
      </button>
      {editingName ? (
        <div className="adj-name-edit">
          <div className="adj-row">
            <div className="adj-field">
              <label className="adj-label" htmlFor="jn-first">First name</label>
              <input id="jn-first" className="adj-in" autoComplete="given-name" maxLength={50} value={nameDraft.fname} disabled={disabled}
                onChange={(e) => setNameDraft({ ...nameDraft, fname: e.target.value })} onKeyDown={enterIsDone} enterKeyHint="done" />
            </div>
            <div className="adj-field">
              <label className="adj-label" htmlFor="jn-last">Last name</label>
              <input id="jn-last" className="adj-in" autoComplete="family-name" maxLength={50} value={nameDraft.lname} disabled={disabled}
                onChange={(e) => setNameDraft({ ...nameDraft, lname: e.target.value })} onKeyDown={enterIsDone} enterKeyHint="done" />
            </div>
          </div>
          {nameError && <p className="adj-error" role="alert">{nameError}</p>}
          <div className="adj-who-acts">
            <button type="button" className="adj-btn ghost" onClick={() => setNameDraft(null)} disabled={disabled}>Cancel</button>
            <button type="button" className="adj-btn primary" onClick={doneName} disabled={disabled}>Done</button>
          </div>
        </div>
      ) : (
        <p className="adj-name">
          {name}
          {onChooseName && (
            <button type="button" className="adj-name-pencil" onClick={startName} disabled={disabled} aria-label="Change how your name is shown">
              <PencilIcon />
            </button>
          )}
        </p>
      )}
      {(profile?.place || profile?.location) && <p className="adj-place">{profile.place || profile.location}</p>}
      <div className="adj-who-acts">
        <button type="button" className="adj-btn ghost" onClick={choose} disabled={disabled}>
          {preview ? "Change photo" : "Add your photo"}
        </button>
        {source && preview === urls.current.preview && (
          <button type="button" className="adj-btn ghost" onClick={() => setEditing(true)} disabled={disabled}>Adjust</button>
        )}
      </div>
      <p className="adj-hint">{prompt || "A clear head-and-shoulders photo, shown with your name on event pages and results."}</p>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={pick} />
      {editing && (pending || source) && <PhotoEditor src={pending || source} initial={pending ? null : framing} onDone={framed} onCancel={cancel} />}
    </div>
  );
};

// /adjudicator/profile: change the photo, or how the name is shown.
const AdjudicatorProfileForm = ({ initial, onSaved }) => {
  const [current, setCurrent] = useState(initial?.photo || "");
  const [picked, setPicked] = useState(null);
  const [profile, setProfile] = useState(initial);
  const [chosenName, setChosenName] = useState(null);
  const [nameEditing, setNameEditing] = useState(false);
  useEffect(() => { if (!nameEditing) setError((e) => (e === FINISH_NAME ? "" : e)); }, [nameEditing]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // an open name edit is finished first, never silently dropped
    if (nameEditing) return setError(FINISH_NAME);
    if (!picked && !chosenName) return;
    setBusy(true);
    setError("");
    try {
      // the photo on file goes back with a name-only change (the API needs one)
      const res = picked
        ? await savePhoto(picked.blob, withChosenName(chosenName))
        : await saveProfile({ photo: current, ...withChosenName(chosenName) });
      setCurrent(res?.profile?.photo || picked?.preview || current);
      // the saved name shows even if the reply's profile lacks it
      setProfile((p) => ({
        ...(res?.profile || p),
        ...(chosenName ? { ...chosenName, name: `${chosenName.fname} ${chosenName.lname}` } : {}),
      }));
      setPicked(null);
      setChosenName(null);
      setSaved(true);
      if (onSaved) onSaved(res?.profile);
    } catch (err) {
      setError(errorText(err, "Your changes weren't saved. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="adj-form" onSubmit={submit} noValidate>
      <PhotoPicker
        preview={picked?.preview || current}
        profile={profile}
        onPick={(p) => { setPicked(p); setSaved(false); }}
        onError={setError}
        disabled={busy}
        chosenName={chosenName}
        onChooseName={(n) => { setChosenName(n); setSaved(false); }}
        onNameEditing={setNameEditing}
      />
      {error && <p className="adj-error" role="alert">{error}</p>}
      {saved && !error && <p className="adj-ok" role="status">Saved.</p>}
      <button type="submit" className="adj-btn primary adj-submit" disabled={busy || (!picked && !chosenName && !nameEditing)}>
        {busy ? "Saving…" : "Save"}
      </button>
    </form>
  );
};

export default AdjudicatorProfileForm;
